import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { 
  fetchProductsAsync, 
  filterProductsAsync,
  setSearchTerm,
  setFilter,
  setCurrentPage,
  resetFilters
} from '../features/products/productsSlice';
import WholesalerNavbar from '../features/wholesaler/components/WholesalerNavbar';
import ProductCard from '../features/wholesaler/components/ProductCard';
import SearchFilters from '../features/wholesaler/components/SearchFilters';
import Pagination from '../features/components/Pagination';
import { productsPerPageWebsite, productsPerPageMobile } from '../app/constants';
import Footer from '../features/components/Footer';
import { useNavigate } from 'react-router-dom';

const WholesalerInventory = () => {
  const dispatch = useDispatch();
  const { shopName } = useSelector((state) => state.auth);
  const { 
    products,
    filteredProducts, 
    currentPage, 
    searchTerm,
    filters,
    loading,
    error
  } = useSelector((state) => state.products);
  const navigate = useNavigate();

  // Fetch products on component mount
  useEffect(() => {
    dispatch(fetchProductsAsync());
  }, [dispatch]);

  // Calculate pagination
  const productsPerPage = (window.innerWidth >= 1024) ? productsPerPageWebsite : (window.innerWidth >= 640) ? productsPerPageMobile : productsPerPageMobile;
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  // Handle search
  const handleSearch = (term) => {
    dispatch(setSearchTerm(term));
    dispatch(filterProductsAsync({
      products,
      searchTerm: term,
      filters
    }));
  };

  // Handle filter change
  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    dispatch(setFilter({ filterType, value }));
    dispatch(filterProductsAsync({
      products,
      searchTerm,
      filters: newFilters
    }));
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    dispatch(setCurrentPage(pageNumber));
  };

  const handleResetFilter = () => {
    dispatch(resetFilters());
    dispatch(filterProductsAsync({
      products,
      searchTerm: '',
      filters: { category: '', purity: '', sort: '' }
    }));
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <WholesalerNavbar />
      
      <div className="pt-20">
        <div className="container mx-auto px-4 py-8">
          {/* Company Name Title and Add Product Button */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row items-center gap-4 relative">
              <div className="text-center w-full">
                <h1 className="text-3xl font-bold text-gray-800">
                  {shopName || 'Your Company'}'s Inventory
                </h1>
                <p className="text-gray-600 mt-2">
                  Manage your gold products inventory
                </p>
              </div>
              <button
                onClick={() => navigate('/wholesaler/inventory/add')}
                className="sm:absolute sm:right-0 inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 shadow-sm transition-colors duration-200"
              >
                <svg 
                  className="mr-2 -ml-1 h-5 w-5" 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 20 20" 
                  fill="currentColor"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" 
                    clipRule="evenodd" 
                  />
                </svg>
                Add Product
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <SearchFilters 
            searchTerm={searchTerm}
            filters={filters}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
          />

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-12 bg-red-50 rounded-lg">
              <p className="text-red-600">{error}</p>
              <p className="mt-2 text-sm text-gray-600">
                Please try logging in again or contact support if the issue persists.
              </p>
            </div>
          )}

          {/* Product Count */}
          {!loading && !error && (
            <div className="mb-4 text-sm text-gray-600">
              Showing {currentProducts.length} of {filteredProducts.length} products
            </div>
          )}

          {/* Product Grid */}
          {!loading && !error && currentProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {currentProducts.map((product) => (
                <ProductCard key={product.productId} product={product} />
              ))}
            </div>
          ) : !loading && !error && (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <svg 
                className="mx-auto h-12 w-12 text-gray-400" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">No products found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search or filter to find what you're looking for.
              </p>
            </div>
          )}

          {/* Pagination */}
          {!loading && !error && totalPages > 1 && (
            <div className="mt-8">
              <Pagination 
                currentPage={currentPage} 
                totalPages={totalPages} 
                onPageChange={handlePageChange} 
              />
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default WholesalerInventory;